import { useState, useRef, useCallback, useEffect } from 'react';

/**
 * Hook para drag-to-fill tipo Excel en la tabla de precios.
 *
 * Ciclo:
 * 1. mousedown en drag handle -> captura fila/columna/valor origen
 * 2. mousemove -> detecta fila actual via data-row, actualiza highlight
 * 3. mouseup -> aplica valor a rango, batch setState
 *
 * @param {Object} params
 * @param {React.MutableRefObject} params.productosRef - ref al array de productosFiltrados
 * @param {Function} params.getValorFromRef - (producto, campo) => valor
 * @param {Function} params.getCostoFromRef - (producto) => costo
 * @param {Function} params.getPorcentajeFromRef - (productoId, campo) => porcentaje
 * @param {Function} params.getPorcentajeBaseFromRef - (producto) => porcentajeBase
 * @param {Function} params.calcularPrecioConGanancia - (costo, porcentaje) => precio
 * @param {Function} params.calcularPorcentajeGanancia - (costo, precio) => porcentaje
 * @param {Function} params.batchApplyChanges - (editedMap, porcentajesMap) => void
 */
export default function useDragToFill({
  productosRef,
  getValorFromRef,
  getCostoFromRef,
  getPorcentajeFromRef,
  getPorcentajeBaseFromRef,
  calcularPrecioConGanancia,
  calcularPorcentajeGanancia,
  batchApplyChanges
}) {
  const [dragState, setDragState] = useState(null);
  // dragState: { originRow, originCol, originProductoId, originValue, originFieldType, currentRow }

  const isDragging = useRef(false);
  const dragDataRef = useRef(null);

  const handleDragHandleMouseDown = useCallback((e, rowIndex, colName, productoId) => {
    e.preventDefault();
    e.stopPropagation();

    const productos = productosRef.current;
    const producto = productos[rowIndex];
    if (!producto) return;

    // Determine field type and value
    let fieldType, originValue, originCampo;

    if (colName === 'costo') {
      fieldType = 'costo';
      originValue = getCostoFromRef(producto);
      originCampo = 'costo_precios';
    } else if (colName === 'cantidad_descuento') {
      fieldType = 'cantidad_descuento';
      originValue = producto.cantidad_descuento || 0;
      // Check editedData
      originCampo = 'cantidad_descuento';
    } else if (colName === 'porcentaje_base') {
      fieldType = 'porcentaje_base';
      originValue = getPorcentajeBaseFromRef(producto);
      originCampo = 'porcentaje_base';
    } else if (colName === 'precio_publico') {
      fieldType = 'precio_publico';
      // Get the displayed value
      const precioLista = getValorFromRef(producto, 'precio_lista');
      const pctIncre = getPorcentajeBaseFromRef(producto);
      if (precioLista > 0 && pctIncre > 0) {
        originValue = Math.round(precioLista * (1 + pctIncre / 100) * 100) / 100;
      } else {
        originValue = precioLista;
      }
      originCampo = 'precio_publico';
    } else if (colName.endsWith('_precio')) {
      const campo = colName.replace('_precio', '');
      fieldType = 'precio';
      originValue = getValorFromRef(producto, campo);
      originCampo = campo;
    } else if (colName.endsWith('_pct')) {
      const campo = colName.replace('_pct', '');
      fieldType = 'porcentaje';
      originValue = parseFloat(getPorcentajeFromRef(producto.id, campo)) || 0;
      originCampo = campo;
    }

    const data = {
      originRow: rowIndex,
      originCol: colName,
      originProductoId: productoId,
      originValue,
      originFieldType: fieldType,
      originCampo,
      currentRow: rowIndex
    };

    isDragging.current = true;
    dragDataRef.current = data;
    setDragState(data);

    // Disable text selection during drag
    document.body.style.userSelect = 'none';
    document.body.classList.add('drag-filling');
  }, [productosRef, getCostoFromRef, getValorFromRef, getPorcentajeFromRef, getPorcentajeBaseFromRef]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging.current || !dragDataRef.current) return;

    // Find the row under the mouse
    const el = document.elementFromPoint(e.clientX, e.clientY);
    if (!el) return;

    const tr = el.closest('tr[data-row]');
    if (!tr) return;

    const row = parseInt(tr.dataset.row, 10);
    if (isNaN(row)) return;

    if (row !== dragDataRef.current.currentRow) {
      dragDataRef.current = { ...dragDataRef.current, currentRow: row };
      setDragState({ ...dragDataRef.current });
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!isDragging.current || !dragDataRef.current) {
      isDragging.current = false;
      dragDataRef.current = null;
      return;
    }

    const data = dragDataRef.current;
    isDragging.current = false;
    dragDataRef.current = null;

    document.body.style.userSelect = '';
    document.body.classList.remove('drag-filling');

    const { originRow, currentRow, originValue, originFieldType, originCampo } = data;

    // Determine range
    const startRow = Math.min(originRow, currentRow);
    const endRow = Math.max(originRow, currentRow);

    // Skip if no range (same row)
    if (startRow === endRow) {
      setDragState(null);
      return;
    }

    const productos = productosRef.current;
    const editedBatch = {};
    const porcentajesBatch = {};

    for (let i = startRow; i <= endRow; i++) {
      if (i === originRow) continue; // Don't overwrite origin
      const prod = productos[i];
      if (!prod) continue;

      const productoId = prod.id;
      const costo = getCostoFromRef(prod);

      switch (originFieldType) {
        case 'costo': {
          // Copy same cost, recalculate all prices using each row's existing percentages
          const costoNuevo = originValue;
          const costoAnterior = costo;
          const cambiosCosto = { costo_precios: costoNuevo };
          const pctsCosto = {};
          const campos = ['precio_lista', 'precio1', 'precio2', 'precio3', 'precio4', 'precio5', 'precio6'];

          if (costoNuevo > 0) {
            campos.forEach(campo => {
              const precioActual = getValorFromRef(prod, campo);
              const porcentajeActual = (costoAnterior > 0 && precioActual > 0)
                ? calcularPorcentajeGanancia(costoAnterior, precioActual) : 0;
              pctsCosto[campo] = porcentajeActual.toString();
              if (porcentajeActual > 0) {
                cambiosCosto[campo] = calcularPrecioConGanancia(costoNuevo, porcentajeActual);
              }
            });
          }

          editedBatch[productoId] = { ...(editedBatch[productoId] || {}), ...cambiosCosto };
          porcentajesBatch[productoId] = { ...(porcentajesBatch[productoId] || {}), ...pctsCosto };
          break;
        }
        case 'cantidad_descuento': {
          // Copy same value
          editedBatch[productoId] = { cantidad_descuento: originValue };
          break;
        }
        case 'porcentaje_base': {
          // Copy same %Incre
          editedBatch[productoId] = { porcentaje_base: originValue };
          break;
        }
        case 'precio_publico': {
          // Copy value, calculate %Incre reversal
          const precioLista = getValorFromRef(prod, 'precio_lista');
          if (precioLista > 0 && originValue > 0) {
            const nuevoIncre = Math.round(((originValue - precioLista) / precioLista) * 100 * 100) / 100;
            editedBatch[productoId] = { porcentaje_base: nuevoIncre };
          }
          break;
        }
        case 'porcentaje': {
          // Copy same %, calculate price based on EACH row's cost
          if (costo <= 0) continue; // Skip rows with 0 cost
          const nuevoPrecio = calcularPrecioConGanancia(costo, originValue);
          editedBatch[productoId] = {
            ...(editedBatch[productoId] || {}),
            [originCampo]: nuevoPrecio
          };
          porcentajesBatch[productoId] = {
            ...(porcentajesBatch[productoId] || {}),
            [originCampo]: originValue.toString()
          };
          break;
        }
        case 'precio': {
          // Copy literal price, recalculate percentage
          editedBatch[productoId] = {
            ...(editedBatch[productoId] || {}),
            [originCampo]: originValue
          };
          if (costo > 0 && originValue > 0) {
            const pct = calcularPorcentajeGanancia(costo, originValue);
            porcentajesBatch[productoId] = {
              ...(porcentajesBatch[productoId] || {}),
              [originCampo]: pct.toString()
            };
          }
          break;
        }
        default:
          break;
      }
    }

    // Batch apply
    batchApplyChanges(editedBatch, porcentajesBatch);
    setDragState(null);
  }, [productosRef, getCostoFromRef, getValorFromRef, calcularPrecioConGanancia, calcularPorcentajeGanancia, batchApplyChanges]);

  // Global listeners for mousemove/mouseup
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  // Helper to check if a row is in the drag highlight range
  const isRowInDragRange = useCallback((rowIndex) => {
    if (!dragState) return false;
    const { originRow, currentRow } = dragState;
    const minR = Math.min(originRow, currentRow);
    const maxR = Math.max(originRow, currentRow);
    return rowIndex >= minR && rowIndex <= maxR && rowIndex !== originRow;
  }, [dragState]);

  return {
    dragState,
    handleDragHandleMouseDown,
    isRowInDragRange
  };
}
